import { Controller } from '@hotwired/stimulus';
import { createFocusTrap } from 'focus-trap';
import { useMatchMedia } from 'stimulus-use';

import mediaQueries from '../utils/media-queries';
import { transition } from '../utils/transition';

export default class extends Controller {
    static targets = ['drawer', 'overlay'];

    static outlets = ['slideover'];

    static values = {
        open: { type: Boolean, default: false },
        // Allowed values: 'right', 'left'
        side: { type: String, default: 'right' },
        // Allowed values: 'on', 'off', 'mobile'
        withFocusTrap: { type: String, default: 'on' },
        // Breakpoint at which the slideover switches from mobile to desktop mode.
        desktopBreakpoint: { type: String, default: 'md' },
    };

    #trap = null;

    initialize() {
        useMatchMedia(this, { mediaQueries: mediaQueries[this.desktopBreakpointValue] });

        document.body.classList.add('has-[[data-slideover-open-value=true]]:tw-container-type-normal');
        document.body.classList.add('has-[[data-slideover-open-value=true]]:tw-overflow-hidden');
    }

    connect() {
        this.#applyTransitionAttributes();

        this.element.addEventListener('open-slideover', this.open);
        this.element.addEventListener('close-slideover', this.close);
        this.element.addEventListener('keydown', this.#closeOnEscape);
    }

    disconnect() {
        this.element.removeEventListener('open-slideover', this.open);
        this.element.removeEventListener('close-slideover', this.close);
        this.element.removeEventListener('keydown', this.#closeOnEscape);
    }

    isDesktop() {
        this.#setupFocusTrap('desktop');
    }

    isMobile() {
        this.#setupFocusTrap('mobile');
    }

    open = async () => {
        this.openValue = true;

        // In case of nested drawers, we need to prevent the parent drawer from scrolling
        const parentDrawer = this.element.closest('[data-slideover-target="drawer"]');
        if (parentDrawer) {
            let isScrolling;
            const onScroll = () => {
                clearTimeout(isScrolling);
                isScrolling = setTimeout(() => {
                    parentDrawer?.classList.add('tw-overflow-hidden');
                    parentDrawer.removeEventListener('scroll', onScroll);
                }, 150);
            };
            parentDrawer.addEventListener('scroll', onScroll, false);

            parentDrawer.scrollTo({ top: 0 });
        }

        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.remove('tw-hidden');
        }

        await transition(this.drawerTarget, true);

        if (this.drawerTarget.id) {
            document.querySelectorAll(`[aria-controls="${this.drawerTarget.id}"]`).forEach((element) => {
                element.ariaExpanded = 'true';
            });
        }

        this.dispatch('open');

        this.#trap?.activate();
    };

    close = async () => {
        const parentDrawer = this.element.closest('[data-slideover-target="drawer"]');

        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add('tw-hidden');
        }

        await transition(this.drawerTarget, false);

        if (this.drawerTarget.id) {
            document.querySelectorAll(`[aria-controls="${this.drawerTarget.id}"]`).forEach((element) => {
                element.ariaExpanded = 'false';
            });
        }

        parentDrawer?.classList.remove('tw-overflow-hidden');

        this.element.querySelectorAll('[data-slideover-open-value="true"]').forEach((nestedSlideover) => {
            nestedSlideover.dispatchEvent(new Event('close-slideover'));
        });

        this.openValue = false;

        this.dispatch('close');

        this.#trap?.deactivate();
    };

    openSlideover({ params }) {
        if (typeof params.slideoverId === 'undefined') {
            const slideover = this.slideoverOutlets.at(0);

            if (slideover) {
                slideover.open();
            }

            return;
        }

        this.slideoverOutlets.forEach((slideover) => {
            if (slideover.element.id !== params.slideoverId) {
                return;
            }

            slideover.open();
        });
    }

    #applyTransitionAttributes() {
        if (!this.hasDrawerTarget) {
            return;
        }

        const animationClasses = 'tw-transition-transform tw-ease-in-out tw-duration-300';
        const hiddenClasses = this.sideValue === 'right' ? 'tw-translate-x-full' : 'tw--translate-x-full';
        const shownClasses = 'tw-translate-x-0';

        if (!this.drawerTarget.hasAttribute('data-transition-enter')) {
            this.drawerTarget.setAttribute('data-transition-enter', animationClasses);
        }

        if (!this.drawerTarget.hasAttribute('data-transition-enter-from')) {
            this.drawerTarget.setAttribute('data-transition-enter-from', hiddenClasses);
        }

        if (!this.drawerTarget.hasAttribute('data-transition-enter-to')) {
            this.drawerTarget.setAttribute('data-transition-enter-to', shownClasses);
        }

        if (!this.drawerTarget.hasAttribute('data-transition-leave')) {
            this.drawerTarget.setAttribute('data-transition-leave', animationClasses);
        }

        if (!this.drawerTarget.hasAttribute('data-transition-leave-from')) {
            this.drawerTarget.setAttribute('data-transition-leave-from', shownClasses);
        }

        if (!this.drawerTarget.hasAttribute('data-transition-leave-to')) {
            this.drawerTarget.setAttribute('data-transition-leave-to', hiddenClasses);
        }
    }

    #closeOnEscape = async (event) => {
        if (event.key === 'Escape' && this.openValue) {
            await this.close();
        }
    };

    #setupFocusTrap(viewport) {
        if (this.withFocusTrapValue === 'off') {
            this.#trap?.deactivate();
            this.#trap = null;

            return;
        }

        const factory = () => createFocusTrap(this.hasDrawerTarget ? this.drawerTarget : this.element, {
            allowOutsideClick: true,
        });

        if (!this.#trap && this.withFocusTrapValue === 'on') {
            this.#trap = factory();

            return;
        }

        if (this.withFocusTrapValue === viewport) {
            if (!this.#trap) {
                this.#trap = factory();
            }
        } else {
            this.#trap?.deactivate();
            this.#trap = null;
        }
    }
}
